import { createContext, useContext, useEffect, useMemo, useState } from 'react'

// Client-side station library + visibility prefs, mirroring _units.jsx:
// one localStorage blob, SSR-safe (state starts empty so server render == first
// client render; a mount effect loads storage and re-renders, no hydration
// mismatch). Holds the user's favorites (unlimited) and recents (max 3/type),
// plus the home-list toggles.
//
// Kinds are 'tide' | 'current' | 'chart'.
//
// Entry shape is minimal — { id, name } — and for stations the URL is derived
// per kind: /tide_stations/{id} or /current_stations/{id}.
//
// CHARTS CARRY A THIRD FIELD, `slug`, and the reason is worth keeping. A chart's
// URL is /charts/<vendor>-<slug>, and slugs MOVE: the 2026-09-04 title clean-up
// renamed 29 of them in one go. Key a favorite on the slug and that chart stops
// matching its own page after a rename — the star reads empty and toggling adds
// a duplicate pointing at a stale URL. So identity is the database id, which is
// permanent and never appears in a URL, and the slug rides alongside purely as
// the link. recordRecent refreshes it on every visit, so stored entries heal
// themselves; and a stale one still works regardless, because the chart
// controller 301s any wrong-slug URL to the canonical one.
//
// Adding 'chart' needs no migration: readStored validates each list
// independently, so a blob written before this shipped reads back with empty
// chart lists.

const STORAGE_KEY = 'tnm.stations'
const RECENTS_MAX = 3
const KINDS = ['tide', 'current', 'chart']

const emptyByKind = () => Object.fromEntries(KINDS.map((kind) => [kind, []]))

// SHOW COUNTRIES LIST: Auto, Always, Never (Randy, 2026-09-12, respec).
//
// AUTO is the default and it is not "on". The By Country list renders only when
// the page would otherwise be EMPTY — no favourites, no recents, and no location
// of any kind — and disappears the moment any of those exists. So for most
// readers Auto means hidden; the list is the floor under an empty page, not a
// section that competes with real content. The rule itself lives in
// home/index.jsx (`countriesVisibleUnder`), because it needs the page's content.
//
// ALWAYS and NEVER are explicit and beat the rule in both directions, on every
// surface the list appears on and across visits.
//
// STORED AS A STRING, and the old boolean is migrated rather than dropped: a
// reader who had previously turned By Country off has `countriesEnabled: false`
// in their blob and meant Never, so they get Never. `true` was the old default
// rather than a choice — the toggle shipped on — so it maps to Auto rather than
// Always, which is the reading that does not resurrect a list for someone who
// never asked for one.
export const COUNTRIES_MODES = ['auto', 'always', 'never']
export const COUNTRIES_DEFAULT = 'auto'

function asMode(raw) {
  if (COUNTRIES_MODES.includes(raw)) return raw
  // The 2026-09-12 morning boolean, and the pre-respec shape.
  if (raw === false) return 'never'
  return COUNTRIES_DEFAULT
}

// The cookie exists ONLY so the server can render the right thing first time.
//
// Everything else here is localStorage, because everything else here is read
// after mount and a wrong first paint costs nothing. By Country is different:
// it is server-rendered (deliberately — it is the crawlable half of the home
// page), so a client-only pref means the list paints and then vanishes.
//
// WHAT THE SERVER CAN AND CANNOT DO WITH IT. `always` and `never` it can honour
// outright, and those are the two that would flash. `auto` it cannot decide at
// all — the rule reads favourites, recents and location, none of which the
// server has — so it renders nothing and the client adds the list in the rare
// auto-and-empty case. An add-in on an empty page is the mildest of the three
// possible flashes, and the only one left.
//
// NOT httponly and not a session cookie: the client is the only writer, and the
// choice has to outlive the tab. It carries one word and no identity.
//
// The units pref does NOT do this, despite being the obvious precedent — it is
// localStorage-only and flashes on any server-rendered surface. This is a new
// pattern here (the only other server-read cookie is `locale`), so if it earns
// its keep, units is the next one to take it.
export const COUNTRIES_COOKIE = 'tnm_countries'

function writeCountriesCookie(mode, doc = typeof document === 'undefined' ? null : document) {
  if (!doc) return
  try {
    doc.cookie = `${COUNTRIES_COOKIE}=${mode}; path=/; max-age=${365 * 24 * 60 * 60}; samesite=lax`
  } catch {
    /* cookies disabled: the pref still works, the first paint just flashes */
  }
}

// `null` for absent or unrecognised, so the caller can tell "nothing stated"
// from a mode. The value is ANCHORED at both ends: an earlier cut matched the
// first character of `tnm_countries=11` and read a garbage value as a choice,
// which is the one thing reader-supplied input must never become.
export function readCountriesCookie(doc = typeof document === 'undefined' ? null : document) {
  const match = doc?.cookie?.match(new RegExp(`(?:^|;\\s*)${COUNTRIES_COOKIE}=([a-z]+)(?:;|$)`))
  if (!match) return null
  return COUNTRIES_MODES.includes(match[1]) ? match[1] : null
}

// THE AUTO RULE, in one place and testable without a page.
//
// It lives here rather than in home/index.jsx because it is the meaning of the
// pref, and the pref lives here — a rule buried in a 400-line component is a
// rule nobody can check. The component supplies the three facts and nothing
// else decides.
//
// `hasLocation` is LOCATION OF ANY KIND: precise GPS, or the coarse
// timezone/locale fix from _coarseLocation.js. That is the brief's wording and
// it has a consequence worth stating — almost every browser reports a time zone
// this app can map, so under Auto the list is hidden for almost everyone, and
// what it really covers is a reader in an unmapped region with an empty
// library. That is the floor it is meant to be.
export function countriesVisibleUnder({ mode, hasFavorites, hasRecents, hasLocation }) {
  if (mode === 'always') return true
  if (mode === 'never') return false
  return !hasFavorites && !hasRecents && !hasLocation
}

const EMPTY = {
  favorites: emptyByKind(),
  recents: emptyByKind(),
  recentsEnabled: true,
  currentsEnabled: true,
  chartsEnabled: true,
  // Auto, which for most readers means hidden. See COUNTRIES_MODES above.
  countriesMode: COUNTRIES_DEFAULT,
}

const listsFrom = (raw) =>
  Object.fromEntries(KINDS.map((kind) => [kind, Array.isArray(raw?.[kind]) ? raw[kind] : []]))

function readStored() {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') || {}
    return {
      favorites: listsFrom(raw.favorites),
      recents: listsFrom(raw.recents),
      recentsEnabled: raw.recentsEnabled !== false,
      currentsEnabled: raw.currentsEnabled !== false,
      chartsEnabled: raw.chartsEnabled !== false,
      // `countriesMode` first, then the retired boolean it replaced.
      countriesMode: asMode(raw.countriesMode ?? raw.countriesEnabled),
    }
  } catch {
    return EMPTY
  }
}

function persist(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    /* private mode / no storage */
  }
}

const StationsContext = createContext({
  ...EMPTY,
  isFavorite: () => false,
  toggleFavorite: () => {},
  recordRecent: () => {},
  setPref: () => {},
  clearRecents: () => {},
})

export function StationsProvider({ children }) {
  const [state, setState] = useState(EMPTY)

  useEffect(() => {
    const stored = readStored()
    setState(stored)
    // HEAL THE COOKIE for anyone who chose before it existed. Their choice is
    // in localStorage and the server cannot see it, so their first paint of
    // every visit would show the list and then hide it. Writing it once here
    // makes the second visit clean, and costs nothing when they already agree.
    // ONLY for a reader who actually chose something. An earlier cut healed off
    // the resolved value and so set a cookie on every first visit in the world;
    // the default needs no cookie, because a server with no cookie already
    // renders the default.
    if (
      stored.countriesMode !== COUNTRIES_DEFAULT &&
      readCountriesCookie() !== stored.countriesMode
    ) {
      writeCountriesCookie(stored.countriesMode)
    }
  }, [])

  // All mutators base off the *latest persisted storage* (read-modify-write,
  // like _units.jsx setUnit), never stale React state — so a mutation that fires
  // before hydration (e.g. recordRecent in a mount effect) can't clobber stored
  // data. localStorage is synchronous, so sequential mutations compose.
  const update = (fn) => {
    const next = fn(readStored())
    persist(next)
    setState(next)
  }

  // `slug` is only ever set for charts; omitting the key entirely for stations
  // keeps their stored entries byte-identical to what shipped before.
  const entryOf = ({ id, name, slug }) => (slug ? { id, name, slug } : { id, name })

  const isFavorite = (kind, id) => (state.favorites[kind] || []).some((s) => s.id === id)

  const toggleFavorite = (kind, item) =>
    update((prev) => {
      const list = prev.favorites[kind] || []
      const present = list.some((s) => s.id === item.id)
      const nextList = present ? list.filter((s) => s.id !== item.id) : [entryOf(item), ...list]
      return { ...prev, favorites: { ...prev.favorites, [kind]: nextList } }
    })

  // Also refreshes a favorite's stored slug, which is where chart entries heal
  // after a rename: visiting the page is what proves the current URL.
  const recordRecent = (kind, item) => {
    if (!item || !item.name || item.id == null) return
    update((prev) => {
      const list = prev.recents[kind] || []
      const nextList = [entryOf(item), ...list.filter((s) => s.id !== item.id)].slice(
        0,
        RECENTS_MAX,
      )
      const favorites = prev.favorites[kind] || []
      return {
        ...prev,
        recents: { ...prev.recents, [kind]: nextList },
        favorites: {
          ...prev.favorites,
          [kind]: favorites.map((s) => (s.id === item.id ? entryOf(item) : s)),
        },
      }
    })
  }

  const setPref = (key, val) => {
    update((prev) =>
      key === 'countriesMode' ? { ...prev, countriesMode: asMode(val) } : { ...prev, [key]: val },
    )
    // The one pref the server needs to know before it renders.
    if (key === 'countriesMode') writeCountriesCookie(asMode(val))
  }

  const clearRecents = (kind) =>
    update((prev) => ({
      ...prev,
      recents: kind ? { ...prev.recents, [kind]: [] } : emptyByKind(),
    }))

  const value = useMemo(
    () => ({
      favorites: state.favorites,
      recents: state.recents,
      recentsEnabled: state.recentsEnabled,
      currentsEnabled: state.currentsEnabled,
      chartsEnabled: state.chartsEnabled,
      countriesMode: state.countriesMode,
      isFavorite,
      toggleFavorite,
      recordRecent,
      setPref,
      clearRecents,
    }),
    [state],
  )

  return <StationsContext.Provider value={value}>{children}</StationsContext.Provider>
}

export const useStations = () => useContext(StationsContext)
